import React, {
  ReactElement,
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback
} from 'react';
import {Link} from 'react-router-dom';
import {Typography} from '@customink/pigment-react';
import {ProductVariantsInner, SizesInner} from '@customink/stores-api-client';
import ProductGrid from '../ProductGrid';
import {useAppSelector, useGroupOrderTimeline} from '../../hooks';
import {selectPrimaryCampaign} from '../../slices/storesSlice';
import ProfitProgressBar from '../OrderStatusBar/ProfitProgressBar';
import GroupProgressBar from '../OrderStatusBar/GroupProgressBar';
import {
  useGetStoreDetailsQuery,
  useGetOrderSummaryQuery,
  useLazyGetStoreProductQuery,
  useGetStoreProductsQuery
} from '../../api/storesApi';
import {
  CloseDatePlaceHolder,
  TrackerPlaceHolder,
  ProductCardPlaceHolder
} from './DashboardPlaceHolder';
import ProductCarousel from '../ProductCarousel';
import StoreProductColorPicker from '../StoreProductColorPicker';
import StoreProductSizePicker from '../StoreProductSizePicker';
import ProductSizing from '../ProductSizing';
import {MobileContext} from '../../containers/MobileProvider';
import {formatCurrency, campaignDaysLeft} from '../../utils/helpers';

type DashboardStatusProps = {
  storeSlug: string;
};

const DashboardStatus: React.FC<DashboardStatusProps> = ({
  storeSlug
}: DashboardStatusProps): ReactElement => {
  useGetStoreDetailsQuery(storeSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const primaryCampaign = useAppSelector(selectPrimaryCampaign);
  const isStoreClosed = storeDetails?.status === 'closed';
  const {closeDateFormatted, daysUntilClose} = useGroupOrderTimeline();
  const campaignId = primaryCampaign && primaryCampaign?.id;
  const mobile = useContext(MobileContext);

  const {isLoading: isCampaignOrderLoading, data: campaignOrderData} =
    useGetOrderSummaryQuery({
      urlSlug: storeSlug,
      campaignId
    });

  const totalCollectedOnline = campaignOrderData?.totalCollectedOnline;
  const totalItemsQuantity = campaignOrderData?.totalItemsQuantity;

  const {data: storeProductResults} = useGetStoreProductsQuery({
    urlSlug: storeSlug,
    allProducts: true
  });

  const activeProducts = useMemo(() => {
    return storeProductResults?.filter(product => product.active) || null;
  }, [storeProductResults]);

  const productId = activeProducts?.length
    ? activeProducts[0].id
    : storeProductResults?.[0]?.id || null;

  const [
    triggerGetStoreProductQuery,
    {data: productData, isLoading: isProductLoading}
  ] = useLazyGetStoreProductQuery();

  useEffect(() => {
    if (productId && storeSlug) {
      triggerGetStoreProductQuery({
        urlSlug: storeSlug,
        productId,
        displayImagesOnly: true
      });
    }
  }, [productId, storeSlug]);

  const [currentVariant, setCurrentVariant] =
    useState<ProductVariantsInner | null>(
      productData?.variants?.length ? productData.variants[0] : null
    );
  const [currentSize, setCurrentSize] = useState<SizesInner | null>(
    productData?.variants?.[0]?.sizes ? productData.variants[0].sizes[0] : null
  );

  const images = currentVariant?.images?.map(item => item.url);

  useEffect(() => {
    if (productData && !currentVariant)
      setCurrentVariant(productData?.variants?.[0]);
  }, [productData, currentVariant]);

  const imageCarousel = useMemo(() => {
    return (
      <div className="carousel">
        {images?.length > 0 ? (
          <ProductCarousel mobile={mobile} urls={images} />
        ) : null}
      </div>
    );
  }, [images, mobile]);

  const CloseDate = () => {
    return (
      <div className="dashboard-status-closeDate-wrapper">
        <div className="closeDate">
          <div className="closeDate-header">
            <Typography className="section-title" variant="subHeading">
              Close Date
            </Typography>
            <Link to={`/${storeSlug}/manage/settings`}>Edit Date</Link>
          </div>
          <div className="closeDate-status">
            {isCampaignOrderLoading || !campaignId ? (
              <CloseDatePlaceHolder />
            ) : (
              <>
                <Typography variant="bodyShort2" className="date" gutterBottom>
                  {closeDateFormatted}
                </Typography>
                {!isStoreClosed ? (
                  <Typography
                    variant="bodyShort1"
                    className="daysLeft"
                    gutterBottom>
                    Open for {campaignDaysLeft(daysUntilClose)}
                  </Typography>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const NoOrderMessage = () => {
    return (
      <div className="tracker-ftux">
        <Typography variant="bodyShort2" className="ftux-title">
          No Orders Yet
        </Typography>
        <Typography variant="bodyShort1" className="ftux-content">
          You’ll be able to track your progress here once an order has been
          placed.
        </Typography>
      </div>
    );
  };
  const RenderOrderStatus = useMemo(() => {
    if (totalItemsQuantity === 0) return <NoOrderMessage />;

    return (
      <div className="tracker-status">
        {/* PENDING: [Phelan Lucky] temporarily hard-coded, remove once fundraiser is over */}
        {storeSlug === 'phelan-lucky' ? (
          <ProfitProgressBar
            storeSlug={storeSlug}
            primaryCampaign={primaryCampaign}
          />
        ) : (
          <GroupProgressBar primaryCampaign={primaryCampaign}>
            <div>
              <Typography variant="bodyShort1" className="amountCollected">
                Amount collected online: {formatCurrency(totalCollectedOnline)}
              </Typography>
            </div>
          </GroupProgressBar>
        )}
      </div>
    );
  }, [totalItemsQuantity]);

  const Tracker = () => {
    return (
      <div className="dashboard-status-tracker-wrapper">
        <div className="tracker">
          <Typography className="section-title" variant="subHeading">
            Order Tracker
          </Typography>
          {isCampaignOrderLoading || !campaignId ? (
            <TrackerPlaceHolder />
          ) : (
            RenderOrderStatus
          )}
        </div>
      </div>
    );
  };

  const MobileStatus = useCallback(() => {
    return (
      <div className="dashboard-status">
        <div className="dashboard-status-product-card-wrapper">
          <div className="productCard">
            {productData?.variants?.length ? (
              <Typography className="section-title" variant="subHeading">
                Available Items: {activeProducts?.length}
              </Typography>
            ) : null}

            {!isProductLoading ? (
              <div className="productCard-wrapper">
                {imageCarousel}
                {!productId || !productData ? null : (
                  <div className="detail">
                    <Typography variant="h6" weight="bold">
                      {productData?.name}
                    </Typography>
                    <Typography variant="subHeading">
                      {`${formatCurrency(Number(productData?.price))} each`}
                    </Typography>

                    <StoreProductColorPicker
                      currentVariant={currentVariant}
                      setCurrentVariant={setCurrentVariant}
                      productId={productId}
                      storeSlug={storeSlug}
                    />

                    <StoreProductSizePicker
                      currentSize={currentSize}
                      setCurrentSize={setCurrentSize}
                      currentVariant={currentVariant}
                      productId={productId}
                      storeSlug={storeSlug}
                      isLayout
                    />
                    <ProductSizing
                      productId={productId}
                      storeSlug={storeSlug}
                    />
                  </div>
                )}
              </div>
            ) : (
              <ProductCardPlaceHolder />
            )}
          </div>
        </div>
        <div className="dashboard-status-date-tracker-col-wrapper">
          <CloseDate />
          <Tracker />
        </div>
      </div>
    );
  }, [
    productData,
    productId,
    isProductLoading,
    imageCarousel,
    currentVariant,
    storeSlug,
    currentSize
  ]);

  return mobile ? (
    <MobileStatus />
  ) : (
    <div className="dashboard-status">
      <ProductGrid.Container>
        <ProductGrid.Item numColLg={1.5} numColMd={1.5} numColSm={1}>
          <div className="dashboard-status-product-card-wrapper">
            <div className="productCard">
              {productData?.variants?.length ? (
                <Typography className="section-title" variant="subHeading">
                  Available Items: {activeProducts?.length}
                </Typography>
              ) : null}

              {!isProductLoading ? (
                <div className="productCard-wrapper">
                  <ProductGrid.Container spacing={5}>
                    <ProductGrid.Item
                      numColLg={2.4}
                      numColMd={2.4}
                      numColSm={2}
                      gridKey="carousel">
                      {imageCarousel}
                    </ProductGrid.Item>
                    <ProductGrid.Item
                      numColLg={1.6}
                      numColMd={1.6}
                      numColSm={2}
                      gridKey="detail">
                      {!productId || !productData ? null : (
                        <div className="detail">
                          <Typography variant="h6" weight="bold">
                            {productData?.name}
                          </Typography>
                          <Typography variant="subHeading">
                            {`${formatCurrency(
                              Number(productData?.price)
                            )} each`}
                          </Typography>

                          <StoreProductColorPicker
                            currentVariant={currentVariant}
                            setCurrentVariant={setCurrentVariant}
                            productId={productId}
                            storeSlug={storeSlug}
                          />

                          <StoreProductSizePicker
                            currentSize={currentSize}
                            setCurrentSize={setCurrentSize}
                            currentVariant={currentVariant}
                            productId={productId}
                            storeSlug={storeSlug}
                            isLayout
                          />
                          <ProductSizing
                            productId={productId}
                            storeSlug={storeSlug}
                          />
                        </div>
                      )}
                    </ProductGrid.Item>
                  </ProductGrid.Container>
                </div>
              ) : (
                <ProductCardPlaceHolder />
              )}
            </div>
          </div>
        </ProductGrid.Item>
        <ProductGrid.Item numColLg={3} numColMd={3} numColSm={1}>
          <div className="dashboard-status-date-tracker-col-wrapper">
            <CloseDate />
            <Tracker />
          </div>
        </ProductGrid.Item>
      </ProductGrid.Container>
    </div>
  );
};

export default DashboardStatus;
